import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Feather } from "@expo/vector-icons";
import moment from "moment";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { DatePickerModal } from "react-native-paper-dates";
import { DefaultTheme, DarkTheme, Provider as PaperProvider } from "react-native-paper";

import { useAppTheme } from "../../hooks/useAppTheme";
import { useTranslation } from "../../utils/useTranslation";
import { translations } from "../../utils/translations";
import { exportDataToExcel } from "../../utils/exportDataToExcel";
import { useColumnResize } from "../Reports/hooks/useColumnResize";
import { getMergedTrackerOrders } from "../../redux/dispatcher/chinaTrackerDispatcher";

const DAY_W = 44; // px per day column
const BAR_H = 24;
const BAR_GAP = 4;
const ROW_PAD = 6;
const LEFT_W = 130; // frozen production-line label column
const MONTH_H = 26;
const DAY_H = 24;
const WINDOW_MONTHS = 3;
const CONTAIN_OVERSCROLL_X =
  Platform.OS === "web" ? ({ overscrollBehaviorX: "contain" } as any) : null;
const CONTAIN_OVERSCROLL_Y =
  Platform.OS === "web" ? ({ overscrollBehaviorY: "contain" } as any) : null;

const ORDER_STATUSES = [
  "On Time",
  "At Risk",
  "Delayed",
  "Interrupted",
  "Planned",
  "No Data",
];

const STATUS_LABELS: Record<string, keyof typeof translations> = {
  "On Time": "onTime",
  "At Risk": "atRisk",
  Delayed: "delayed",
  Interrupted: "interrupted",
  Planned: "planned",
  "No Data": "noData",
};

const LIST_COLUMNS: Array<{
  key: string;
  labelKey: keyof typeof translations;
  width: number;
  align: "left" | "right";
}> = [
  { key: "msRef", labelKey: "msRef", width: 100, align: "left" },
  { key: "orderNumber", labelKey: "orderNumber", width: 120, align: "left" },
  { key: "itemCode", labelKey: "itemCode", width: 130, align: "left" },
  { key: "itemName", labelKey: "itemName", width: 300, align: "left" },
  { key: "customer", labelKey: "customer", width: 200, align: "left" },
  { key: "runOrder", labelKey: "runOrder", width: 80, align: "right" },
  { key: "plannedQty", labelKey: "plannedQuantity", width: 120, align: "right" },
  { key: "planStartDay", labelKey: "planStartDay", width: 110, align: "left" },
  { key: "estCompletion", labelKey: "estCompletion", width: 110, align: "left" },
  { key: "actualStartDay", labelKey: "actualStartDay", width: 110, align: "left" },
  { key: "actualCompletionDate", labelKey: "actualCompletionDate", width: 120, align: "left" },
  { key: "etd", labelKey: "etd", width: 100, align: "left" },
  { key: "eta", labelKey: "eta", width: 100, align: "left" },
  { key: "dueDate", labelKey: "dueDate", width: 100, align: "left" },
  { key: "status", labelKey: "orderStatus", width: 120, align: "left" },
];
const LIST_LABEL_SPAN = LIST_COLUMNS.findIndex((column) => column.key === "plannedQty");

const DEFAULT_LIST_WIDTHS: Record<string, number> = LIST_COLUMNS.reduce(
  (acc, column) => ({ ...acc, [column.key]: column.width }),
  {},
);

const RESIZE_HANDLE = {
  position: "absolute" as const,
  right: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: "col-resize",
  zIndex: 2,
};

const RESIZE_HANDLE_LEFT = {
  position: "absolute" as const,
  left: 0,
  top: 0,
  bottom: 0,
  width: 6,
  cursor: "col-resize",
  zIndex: 2,
};

const getStatusColors = (status: string, isDark: boolean) => {
  switch (status) {
    case "On Time":
      return isDark ? { bg: "#14532D", text: "#86EFAC" } : { bg: "#DCFCE7", text: "#166534" };
    case "At Risk":
      return isDark ? { bg: "#713F12", text: "#FEF08A" } : { bg: "#FEF9C3", text: "#854D0E" };
    case "Delayed":
      return isDark ? { bg: "#7C2D12", text: "#FDBA74" } : { bg: "#FFEDD5", text: "#9A3412" };
    case "Interrupted":
      return isDark ? { bg: "#7F1D1D", text: "#FCA5A5" } : { bg: "#FEE2E2", text: "#991B1B" };
    case "Planned":
      return isDark ? { bg: "#0C4A6E", text: "#7DD3FC" } : { bg: "#E0F2FE", text: "#0369A1" };
    default:
      return isDark ? { bg: "#374151", text: "#D1D5DB" } : { bg: "#F3F4F6", text: "#374151" };
  }
};

const formatQty = (value: any) => {
  const number = Number(value);
  return value === null || value === undefined || value === "" || Number.isNaN(number)
    ? "—"
    : number.toLocaleString("en-AU");
};

const runStartTime = (item: trackerRun) =>
  parseTrackerDate(item.run?.actualStartDay ?? item.run?.planStartDay)?.getTime() ??
  Number.MAX_SAFE_INTEGER;

const EXTEND_MONTHS = 1; // months revealed each time a scroll edge is reached
const AUTO_EXTEND_LIMIT_MONTHS = 6;
const EDGE_SLACK = 80; // px from an edge that counts as "scrolled to the end"
const MAX_SPAN_DAYS = 730; // keep the rendered grid to a sane number of cells
const POPOVER_W = 320;
const CARET_H = 7;
const POPOVER_EST_H = 240; // fallback until the popover has been measured once

const BAR_COLORS = [
  "#E8A87C", "#C38D9E", "#85CDCB", "#E27D60", "#41B3A3", "#A2C5AC",
  "#F5B7B1", "#AEC6CF", "#D7BDE2", "#F9E79F", "#A9CCE3", "#B9E3C6",
];

const MS_PER_DAY = 86400000;

const parseTrackerDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toISODate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

const formatDisplayDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;

const formatMobileDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}`;

const formatTrackerDate = (value: string | null | undefined) =>
  value ? moment(value).local().format("DD/MM/YYYY") : "—";

const parseDMY = (text: string) => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!match) return null;
  const [, d, m, y] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const valid =
    date.getFullYear() === Number(y) &&
    date.getMonth() === Number(m) - 1 &&
    date.getDate() === Number(d);
  return valid ? date : null;
};

interface rangePreset {
  key: string;
  labelKey: keyof typeof translations;
  range: () => { start: Date; end: Date };
}

const RANGE_PRESETS: Array<rangePreset> = [
  {
    key: "today",
    labelKey: "today",
    range: () => ({ start: moment().startOf("day").toDate(), end: moment().startOf("day").toDate() }),
  },
  {
    key: "week",
    labelKey: "thisWeek",
    range: () => ({ start: moment().startOf("isoWeek").toDate(), end: moment().endOf("isoWeek").toDate() }),
  },
  {
    key: "month",
    labelKey: "thisMonth",
    range: () => ({ start: moment().startOf("month").toDate(), end: moment().endOf("month").toDate() }),
  },
  {
    key: "quarter",
    labelKey: "thisQuarter",
    range: () => ({ start: moment().startOf("quarter").toDate(), end: moment().endOf("quarter").toDate() }),
  },
  {
    key: "year",
    labelKey: "thisYear",
    range: () => ({ start: moment().startOf("year").toDate(), end: moment().endOf("year").toDate() }),
  },
];

const initialRange = () => {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + WINDOW_MONTHS, 0),
  };
};

const dayDiff = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);

const colorFor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return BAR_COLORS[Math.abs(hash) % BAR_COLORS.length];
};

interface trackerRun {
  order: any;
  run: any;
  key: string;
}

interface positionedRun {
  item: trackerRun;
  startIndex: number;
  endIndex: number;
  lane: number;
}

interface plannedRow {
  key: string;
  name: string;
  bars: Array<positionedRun>;
  height: number;
  top: number;
  barOffset: number;
}

interface popoverAnchor {
  item: trackerRun;
  barTop: number;
  barBottom: number;
  anchorX: number;
  viewLeft: number;
  viewRight: number;
}

const assignLanes = (runs: Array<positionedRun>) => {
  const laneEnds: Array<number> = [];
  runs
    .slice()
    .sort((a, b) => a.startIndex - b.startIndex)
    .forEach((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.startIndex);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = item.endIndex + 1;
      item.lane = lane;
    });
  return laneEnds.length;
};

const compareLines = (a: string, b: string) => {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  if (!Number.isNaN(na)) return -1;
  if (!Number.isNaN(nb)) return 1;
  return a.localeCompare(b);
};

const matchesSearch = (item: trackerRun, terms: Array<string>) => {
  if (terms.length === 0) return true;
  const haystack = [
    item.order?.MSRef,
    item.order?.OrderNumber1,
    item.order?.ItemCode,
    item.order?.ItemName,
    item.order?.BPName,
    item.order?.StateDelivery,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.some((term) => haystack.includes(term));
};

export default function ChinaTrackerGanttScreen({ headerExtra }: { headerExtra?: ReactNode } = {}) {
  const { colors, isDark } = useAppTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const dispatch = useDispatch<any>();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const { approvedOrders, loading, isChinaTrackerLoaded } = useSelector(
    (state: any) => state.chinaTracker,
  );

  const [rangeStart, setRangeStart] = useState<Date>(() => initialRange().start);
  const [rangeEnd, setRangeEnd] = useState<Date>(() => initialRange().end);
  const [activePresetKey, setActivePresetKey] = useState<string>("custom");
  const [isRangePickerOpen, setIsRangePickerOpen] = useState<boolean>(false);
  const [fromText, setFromText] = useState<string>("");
  const [toText, setToText] = useState<string>("");
  const [isFromCalendarOpen, setIsFromCalendarOpen] = useState<boolean>(false);
  const [isToCalendarOpen, setIsToCalendarOpen] = useState<boolean>(false);
  const [searchCriteria, setSearchCriteria] = useState<Array<string>>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<Array<string>>([]);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const searchScrollRef = useRef<any>(null);
  const [selected, setSelected] = useState<popoverAnchor | null>(null);
  const [popoverHeight, setPopoverHeight] = useState<number>(POPOVER_EST_H);
  const [bodyHeight, setBodyHeight] = useState<number>(0);
  const [bodyTop, setBodyTop] = useState<number>(0);
  const bodyScrollRef = useRef<any>(null);
  const [listRowsTop, setListRowsTop] = useState<number>(0);
  const listRowsRef = useRef<any>(null);
  const measureChartTops = () => {
    bodyScrollRef.current?.measureInWindow?.((_x: number, y: number) => setBodyTop(y));
    listRowsRef.current?.measureInWindow?.((_x: number, y: number) => setListRowsTop(y));
  };
  const [gridViewWidth, setGridViewWidth] = useState<number>(0);
  const popoverRef = useRef<View>(null);

  const [viewMode, setViewMode] = useState<"gantt" | "list">("gantt");

  useEffect(() => {
    AsyncStorage.getItem("@ChinaTrackerPlanning_viewMode").then((stored) => {
      if (stored === "list" || stored === "gantt") {
        setViewMode(stored);
      }
    });
  }, []);

  const handleSetViewMode = (mode: "gantt" | "list") => {
    setSelected(null);
    setIsRangePickerOpen(false);
    setViewMode(mode);
    AsyncStorage.setItem("@ChinaTrackerPlanning_viewMode", mode);
  };

  const [expandedKeys, setExpandedKeys] = useState<Array<string>>([]);
  const [listWidths, setListWidths] = useState<Record<string, number>>(DEFAULT_LIST_WIDTHS);
  const startResize = useColumnResize(setListWidths);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [toolbarH, setToolbarH] = useState<number>(0);
  const [listHeaderH, setListHeaderH] = useState<number>(34);
  const [listFooterH, setListFooterH] = useState<number>(34);

  useEffect(() => {
    if (containerSize.width <= 0) return;
    const baseListWidth = LIST_COLUMNS.reduce((sum, column) => sum + column.width, 0);
    const extra = containerSize.width - 24 - 2 - baseListWidth;
    if (extra > 0) {
      setListWidths({ ...DEFAULT_LIST_WIDTHS, itemName: DEFAULT_LIST_WIDTHS.itemName + extra });
    } else if (extra < 0) {
      const ratio = Math.max(800, containerSize.width - 26) / baseListWidth;
      const widths: Record<string, number> = {};
      LIST_COLUMNS.forEach((column) => {
        widths[column.key] = Math.max(60, Math.floor(column.width * ratio));
      });
      setListWidths(widths);
    } else {
      setListWidths({ ...DEFAULT_LIST_WIDTHS });
    }
  }, [containerSize.width]);

  const columnWidth = (key: string) => listWidths[key] ?? DEFAULT_LIST_WIDTHS[key];
  const listLabelWidth = LIST_COLUMNS.slice(0, LIST_LABEL_SPAN).reduce(
    (sum, column) => sum + columnWidth(column.key),
    0,
  );
  const listTailWidth = LIST_COLUMNS.slice(LIST_LABEL_SPAN + 1).reduce(
    (sum, column) => sum + columnWidth(column.key),
    0,
  );

  useEffect(() => {
    if (!selected || Platform.OS !== "web") return;
    const onPointerDown = (event: PointerEvent) => {
      const node: any = popoverRef.current;
      if (node?.contains?.(event.target as Node)) return;
      setSelected(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [selected]);
  const [autoExtended, setAutoExtended] = useState<number>(0);
  const [pendingEdge, setPendingEdge] = useState<"start" | "end" | null>(null);

  useEffect(() => {
    if (!isChinaTrackerLoaded) {
      dispatch(getMergedTrackerOrders({ refresh: false }));
    }
  }, [isChinaTrackerLoaded, dispatch]);

  const onSearchChange = (text: string) => {
    if (text.includes(",")) {
      const before = text.slice(0, text.indexOf(",")).trim();
      if (before) setSearchCriteria((current) => [...current, before]);
      setSearchInput("");
    } else {
      setSearchInput(text);
    }
  };

  const commitSearchInput = () => {
    const trimmed = searchInput.trim();
    if (trimmed) {
      setSearchCriteria((current) => [...current, trimmed]);
      setSearchInput("");
    }
  };

  const totalDays = useMemo(() => dayDiff(rangeStart, rangeEnd) + 1, [rangeStart, rangeEnd]);
  const gridWidth = totalDays * DAY_W;

  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const gridScrollRef = useRef<ScrollView>(null);
  const labelColumnRef = useRef<View>(null);
  const headerRowRef = useRef<View>(null);
  const scrollXRef = useRef(0);
  const pendingLeftShift = useRef(0);
  const scrollHandlerRef = useRef<(event: any) => void>(() => {});

  const onBodyScroll = useRef(
    Animated.event([{ nativeEvent: { contentOffset: { x: scrollAnim } } }], {
      useNativeDriver: false,
      listener: (event: any) => scrollHandlerRef.current(event),
    }),
  ).current;

  const allRuns: Array<trackerRun> = useMemo(() => {
    const orders: Array<any> = approvedOrders ?? [];
    return orders.flatMap((order) =>
      (order?.details ?? []).map((run: any, index: number) => ({
        order,
        run,
        key: run?._id ?? `${order?.Key ?? order?.MSRef ?? "order"}-${index}`,
      })),
    );
  }, [approvedOrders]);

  const dataBounds = useMemo(() => {
    let min: Date | null = null;
    let max: Date | null = null;
    allRuns.forEach(({ run }) => {
      const start =
        parseTrackerDate(run?.actualStartDay) ?? parseTrackerDate(run?.planStartDay);
      const finish =
        parseTrackerDate(run?.actualCompletionDate) ??
        parseTrackerDate(run?.ESTCompletionDate) ??
        start;
      if (start && (!min || start < min)) min = start;
      if (finish && (!max || finish > max)) max = finish;
    });
    return { min, max };
  }, [allRuns]);

  const statusOptions = useMemo(() => {
    const extras = new Set<string>();
    allRuns.forEach((item) => {
      const status = item.order?.OrderStatus;
      if (status && !ORDER_STATUSES.includes(String(status))) {
        extras.add(String(status));
      }
    });
    return [...ORDER_STATUSES, ...Array.from(extras).sort()];
  }, [allRuns]);

  const searchTerms = useMemo(
    () =>
      [...searchCriteria, searchInput.trim()]
        .map((term) => term.toLowerCase())
        .filter(Boolean),
    [searchCriteria, searchInput],
  );

  const days = useMemo(
    () =>
      Array.from({ length: totalDays }, (_, index) => {
        const date = new Date(
          rangeStart.getFullYear(),
          rangeStart.getMonth(),
          rangeStart.getDate() + index,
        );
        const weekday = date.getDay();
        return { date, isWeekend: weekday === 0 || weekday === 6 };
      }),
    [rangeStart, totalDays],
  );

  const months = useMemo(() => {
    const result: Array<{ label: string; span: number; startX: number }> = [];
    let currentX = 0;
    days.forEach(({ date }) => {
      const label = date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
      const last = result[result.length - 1];
      if (last && last.label === label) last.span += 1;
      else result.push({ label, span: 1, startX: currentX });
      currentX += DAY_W;
    });
    return result;
  }, [days]);

  const todayIndex = useMemo(() => {
    const index = dayDiff(rangeStart, new Date());
    return index >= 0 && index < totalDays ? index : -1;
  }, [rangeStart, totalDays]);

  const baseRows: Array<plannedRow> = useMemo(() => {
    const byLine = new Map<string, Array<positionedRun>>();

    allRuns.forEach((item) => {
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(String(item.order?.OrderStatus))) {
        return;
      }
      if (!matchesSearch(item, searchTerms)) return;

      const actualStart = parseTrackerDate(item.run?.actualStartDay);
      const actualFinish = parseTrackerDate(item.run?.actualCompletionDate);
      const start = actualStart ?? parseTrackerDate(item.run?.planStartDay);
      const finish =
        actualFinish ?? parseTrackerDate(item.run?.ESTCompletionDate) ?? start;
      if (!start || !finish) return;

      const rawStart = dayDiff(rangeStart, start);
      const rawEnd = Math.max(rawStart, dayDiff(rangeStart, finish));
      if (rawEnd < 0 || rawStart > totalDays - 1) return;

      const startIndex = Math.max(0, Math.min(rawStart, totalDays - 1));
      const endIndex = Math.max(startIndex, Math.min(rawEnd, totalDays - 1));

      const line = String(item.run?.allocateProductionLine ?? "");
      const key = line === "" || line === "null" || line === "undefined" ? "unassigned" : line;
      if (!byLine.has(key)) byLine.set(key, []);
      byLine.get(key)!.push({ item, startIndex, endIndex, lane: 0 });
    });

    let offset = 0;
    return Array.from(byLine.entries())
      .sort((a, b) => compareLines(a[0], b[0]))
      .map(([key, bars]) => {
        const laneCount = assignLanes(bars);
        const height = Math.max(1, laneCount) * (BAR_H + BAR_GAP) + ROW_PAD * 2;
        const top = offset;
        offset += height;
        return {
          key,
          name: key === "unassigned" ? t("unassigned") : key === "99" ? t("directShipment") : `${t("productionLine")} ${key}`,
          bars,
          height,
          top,
          barOffset: 0,
        };
      });
  }, [allRuns, selectedStatuses, searchTerms, rangeStart, totalDays, t]);

  // Parent navigators don't bound our height, so size the chart body against the window instead of flex.
  const availableBodyHeight = Math.max(200, windowHeight - bodyTop - 24);

  const rows: Array<plannedRow> = useMemo(() => {
    // Leave room for the horizontal scrollbar so stretching never forces a vertical scroll.
    const available = availableBodyHeight - 16;
    const natural = baseRows.reduce((sum, row) => sum + row.height, 0);
    if (baseRows.length === 0 || available <= natural) return baseRows;
    const extra = Math.floor((available - natural) / baseRows.length);
    let offset = 0;
    return baseRows.map((row) => {
      const height = row.height + extra;
      const top = offset;
      offset += height;
      return { ...row, height, top, barOffset: Math.floor(extra / 2) };
    });
  }, [baseRows, availableBodyHeight]);

  const visibleCount = useMemo(
    () => rows.reduce((sum, row) => sum + row.bars.length, 0),
    [rows],
  );

  const listGroups = useMemo(() => {
    const byLine = new Map<string, Array<trackerRun>>();
    allRuns.forEach((item) => {
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(String(item.order?.OrderStatus))) {
        return;
      }
      if (!matchesSearch(item, searchTerms)) return;
      const line = String(item.run?.allocateProductionLine ?? "");
      const key = line === "" || line === "null" || line === "undefined" ? "unassigned" : line;
      if (!byLine.has(key)) byLine.set(key, []);
      byLine.get(key)!.push(item);
    });

    return Array.from(byLine.entries())
      .sort((a, b) => compareLines(a[0], b[0]))
      .map(([key, runs]) => ({
        key,
        name: key === "unassigned" ? t("unassigned") : key === "99" ? t("directShipment") : `${t("productionLine")} ${key}`,
        runs: runs.slice().sort((a, b) => runStartTime(a) - runStartTime(b)),
        plannedQty: runs.reduce((sum, item) => sum + (Number(item.run?.plannedQty) || 0), 0),
      }));
  }, [allRuns, selectedStatuses, searchTerms, t]);

  const listTotals = useMemo(
    () => ({
      plannedQty: listGroups.reduce((sum, group) => sum + group.plannedQty, 0),
      count: listGroups.reduce((sum, group) => sum + group.runs.length, 0),
    }),
    [listGroups],
  );

  const toggleExpanded = (key: string) =>
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  type mobileListItem =
    | { type: "header"; group: (typeof listGroups)[number] }
    | { type: "card"; group: (typeof listGroups)[number]; item: trackerRun };

  const mobileFlatItems = useMemo<Array<mobileListItem>>(() => {
    const items: Array<mobileListItem> = [];
    listGroups.forEach((group) => {
      items.push({ type: "header", group });
      if (expandedKeys.includes(group.key)) {
        group.runs.forEach((item) => items.push({ type: "card", group, item }));
      }
    });
    return items;
  }, [listGroups, expandedKeys]);

  const mobileStickyIndices = useMemo(
    () =>
      mobileFlatItems.reduce<Array<number>>(
        (acc, item, index) => (item.type === "header" ? [...acc, index] : acc),
        [],
      ),
    [mobileFlatItems],
  );

  const toggleStatus = (status: string) =>
    setSelectedStatuses((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status],
    );

  const resetExtension = () => {
    setPendingEdge(null);
    setAutoExtended(0);
  };

  const shiftWindow = (direction: 1 | -1) => {
    resetExtension();
    setRangeStart((current) => moment(current).add(direction, "months").toDate());
    setRangeEnd((current) => moment(current).add(direction, "months").toDate());
    setActivePresetKey("custom");
  };

  const openRangePicker = () => {
    setFromText(formatDisplayDate(rangeStart));
    setToText(formatDisplayDate(rangeEnd));
    setIsRangePickerOpen(true);
  };

  const applyPreset = (preset: rangePreset) => {
    const { start, end } = preset.range();
    resetExtension();
    setRangeStart(start);
    setRangeEnd(end);
    setActivePresetKey(preset.key);
    setIsRangePickerOpen(false);
  };

  const applyCustomRange = () => {
    const from = parseDMY(fromText);
    const to = parseDMY(toText);
    if (!from || !to) return;
    resetExtension();
    setRangeStart(from <= to ? from : to);
    setRangeEnd(from <= to ? to : from);
    setActivePresetKey("custom");
    setIsRangePickerOpen(false);
  };

  const onConfirmFromDate = (params: any) => {
    setIsFromCalendarOpen(false);
    if (params.date) {
      setFromText(moment(params.date).format("DD/MM/YYYY"));
    }
  };

  const onConfirmToDate = (params: any) => {
    setIsToCalendarOpen(false);
    if (params.date) {
      setToText(moment(params.date).format("DD/MM/YYYY"));
    }
  };

  const refreshData = () => {
    setSearchCriteria([]);
    setSearchInput("");
    setSelectedStatuses([]);
    setSelected(null);
    resetExtension();
    const { start, end } = initialRange();
    setRangeStart(start);
    setRangeEnd(end);
    setActivePresetKey("custom");
    dispatch(getMergedTrackerOrders({ refresh: true }));
  };

  const canExtend = (edge: "start" | "end") => {
    if (totalDays >= MAX_SPAN_DAYS) return false;
    const { min, max } = dataBounds;
    return edge === "start"
      ? Boolean(min) && rangeStart > (min as Date)
      : Boolean(max) && rangeEnd < (max as Date);
  };

  const extend = (edge: "start" | "end") => {
    if (!canExtend(edge)) return;
    setActivePresetKey("custom");
    if (edge === "end") {
      setRangeEnd(
        (current) =>
          new Date(current.getFullYear(), current.getMonth() + 1 + EXTEND_MONTHS, 0),
      );
    } else {
      const next = new Date(
        rangeStart.getFullYear(),
        rangeStart.getMonth() - EXTEND_MONTHS,
        1,
      );
      pendingLeftShift.current = dayDiff(next, rangeStart) * DAY_W;
      setRangeStart(next);
    }
  };

  const extendByScroll = (edge: "start" | "end") => {
    if (!canExtend(edge)) return;
    if (autoExtended >= AUTO_EXTEND_LIMIT_MONTHS) {
      setPendingEdge(edge);
      return;
    }
    setAutoExtended((current) => current + EXTEND_MONTHS);
    extend(edge);
  };

  const loadMoreData = () => {
    const edge = pendingEdge;
    if (!edge) return;
    setPendingEdge(null);
    setAutoExtended(0); // fresh allowance for the next six months
    extend(edge);
  };

  scrollHandlerRef.current = (event: any) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
    scrollXRef.current = contentOffset.x;
    if (pendingLeftShift.current !== 0) return; // mid-prepend, ignore
    if (contentOffset.x + layoutMeasurement.width >= contentSize.width - EDGE_SLACK) {
      extendByScroll("end");
    } else if (contentOffset.x <= EDGE_SLACK) {
      extendByScroll("start");
    }
  };

  useEffect(() => {
    if (viewMode !== "gantt") return;
    const frame = requestAnimationFrame(() =>
      gridScrollRef.current?.scrollTo({ x: scrollXRef.current, animated: false }),
    );
    return () => cancelAnimationFrame(frame);
  }, [viewMode]);

  useEffect(() => {
    const shift = pendingLeftShift.current;
    if (shift === 0) return;
    pendingLeftShift.current = 0;
    const target = scrollXRef.current + shift;
    const frame = requestAnimationFrame(() => {
      gridScrollRef.current?.scrollTo({ x: target, animated: false });
      scrollXRef.current = target;
    });
    return () => cancelAnimationFrame(frame);
  }, [rangeStart]);

  const exportToExcel = () => {
    const sourceRows =
      viewMode === "list"
        ? listGroups.map((group) => ({ name: group.name, items: group.runs }))
        : rows.map((row) => ({ name: row.name, items: row.bars.map((bar) => bar.item) }));
    const data = sourceRows.flatMap((row) =>
      row.items.map((item) => ({
        [t("productionLine")]: row.name,
        [t("msRef")]: item.order?.MSRef ?? "",
        [t("orderNumber")]: item.order?.OrderNumber1 ?? "",
        [t("itemCode")]: item.order?.ItemCode ?? "",
        [t("itemName")]: item.order?.ItemName ?? "",
        [t("customer")]: item.order?.BPName ?? "",
        [t("orderStatus")]: item.order?.OrderStatus ?? "",
        [t("runOrder")]: item.run?.runOrder ?? "",
        [t("plannedQuantity")]: item.run?.plannedQty ?? "",
        [t("planStartDay")]: formatTrackerDate(item.run?.planStartDay),
        [t("estCompletion")]: formatTrackerDate(item.run?.ESTCompletionDate),
        [t("actualStartDay")]: formatTrackerDate(item.run?.actualStartDay),
        [t("actualCompletionDate")]: formatTrackerDate(item.run?.actualCompletionDate),
        [t("etd")]: formatTrackerDate(item.run?.ETD),
        [t("eta")]: formatTrackerDate(item.run?.ETA),
        [t("dueDate")]: item.order?.RequestDate ?? "",
      })),
    );

    exportDataToExcel(
      data,
      viewMode === "list"
        ? "china_tracker_planning_list"
        : `china_tracker_planning_${toISODate(rangeStart)}_to_${toISODate(rangeEnd)}`,
      "Planning",
    );
  };

  const contentHeight = useMemo(
    () => rows.reduce((sum, row) => sum + row.height, 0),
    [rows],
  );
  const popoverBottom = selected
    ? selected.barBottom + CARET_H + popoverHeight + 8
    : 0;
  const gridHeight = Math.max(contentHeight, popoverBottom);

  const isBusy = loading || !isChinaTrackerLoaded;

  useEffect(() => {
    if (Platform.OS !== "web" || isBusy || viewMode !== "gantt" || rows.length === 0) return;
    const grid: any = (gridScrollRef.current as any)?.getScrollableNode?.();
    const targets = [labelColumnRef.current, headerRowRef.current] as any[];
    if (!grid) return;

    const onWheel = (event: WheelEvent) => {
      const dx = event.deltaX || (event.shiftKey ? event.deltaY : 0);
      if (Math.abs(dx) <= Math.abs(event.shiftKey ? 0 : event.deltaY)) return;
      event.preventDefault();
      grid.scrollLeft += dx;
    };

    targets.forEach((node) => node?.addEventListener?.("wheel", onWheel, { passive: false }));
    return () =>
      targets.forEach((node) => node?.removeEventListener?.("wheel", onWheel));
  }, [isBusy, viewMode, rows.length === 0]);

  const listRowsMaxHeight = Math.max(
    80,
    listRowsTop > 0
      ? windowHeight - listRowsTop - listFooterH - 24 - 2
      : containerSize.height - 24 - toolbarH - 16 - listHeaderH - listFooterH - 2,
  );

  useEffect(() => {
    const frame = requestAnimationFrame(measureChartTops);
    return () => cancelAnimationFrame(frame);
  }, [toolbarH, listHeaderH, containerSize.width, containerSize.height, windowHeight, viewMode, isBusy, rows.length, listGroups.length]);

  const viewToggleElement = (
    <View style={styles.viewToggle}>
      {(["gantt", "list"] as const).map((mode) => (
        <Pressable
          key={mode}
          onPress={() => handleSetViewMode(mode)}
          style={[styles.viewButton, viewMode === mode && styles.viewButtonActive]}
        >
          <Text
            style={[styles.viewButtonText, viewMode === mode && styles.viewButtonTextActive]}
          >
            {t(mode === "gantt" ? "ganttView" : "listView")}
          </Text>
        </Pressable>
      ))}
    </View>
  );

  const chipContent = statusOptions.map((status) => {
    const active = selectedStatuses.includes(status);
    const label = STATUS_LABELS[status];
    return (
      <Pressable
        key={status}
        onPress={() => toggleStatus(status)}
        style={[styles.chip, active && styles.chipActive]}
      >
        <Text style={[styles.chipText, active && styles.chipTextActive]}>
          {label ? t(label) : status}
        </Text>
      </Pressable>
    );
  });

  const filterChips = isMobile ? (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{ marginTop: 8, width: "100%" }}
      contentContainerStyle={{ flexDirection: "row", gap: 6, paddingHorizontal: 4 }}
    >
      {chipContent}
    </ScrollView>
  ) : (
    <View style={styles.chipGroup}>
      {chipContent}
    </View>
  );

  const loadMoreElement = pendingEdge && (
    <Pressable style={styles.loadMoreButton} onPress={loadMoreData}>
      <Feather
        name={pendingEdge === "start" ? "chevrons-left" : "chevrons-right"}
        size={12}
        color="#1976d2"
      />
      <Text style={styles.loadMoreText}>{t("loadMoreData")}</Text>
    </Pressable>
  );

  return (
    <View
      style={styles.container}
      onLayout={(event) =>
        setContainerSize({
          width: event.nativeEvent.layout.width,
          height: event.nativeEvent.layout.height,
        })
      }
    >
      <View
        style={styles.toolbar}
        onLayout={(event) => setToolbarH(event.nativeEvent.layout.height)}
      >
        <View style={{ width: "100%", flexDirection: "row", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <Text style={styles.title}>{t("chinaOrderTracker")}</Text>
          {headerExtra}
        </View>

        <View style={isMobile ? { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 4, width: "100%", marginBottom: 12, zIndex: 10 } : { zIndex: 10 }}>
          {isMobile && (
            <View style={{ marginRight: viewMode === "list" ? "auto" : 0 }}>
              {viewToggleElement}
            </View>
          )}

          <View style={[styles.rangeGroup, isMobile && { marginBottom: 0, gap: 4, flexShrink: 1 }, viewMode === "list" && styles.hidden]}>
            <Pressable style={[styles.navButton, isMobile && { paddingHorizontal: 8 }]} onPress={() => shiftWindow(-1)}>
              <Text style={styles.navButtonText}>‹</Text>
            </Pressable>

            <View style={styles.rangeFieldWrapper}>
              <Pressable style={[styles.rangeField, isMobile && { paddingHorizontal: 8 }]} onPress={openRangePicker}>
                <Text style={[styles.rangeFieldText, isMobile && { fontSize: 12 }]}>
                  📅 {isMobile ? formatMobileDate(rangeStart) : formatDisplayDate(rangeStart)} → {isMobile ? formatMobileDate(rangeEnd) : formatDisplayDate(rangeEnd)}
                </Text>
              </Pressable>

              {isRangePickerOpen && (
                <>
                  <Pressable
                    style={styles.rangeBackdrop}
                    onPress={() => setIsRangePickerOpen(false)}
                  />
                  <View style={[styles.rangeDropdown, isMobile ? { left: 0 } : { left: 0, minWidth: 260 }]}>
                    {RANGE_PRESETS.map((preset) => {
                      const isActive = activePresetKey === preset.key;
                      return (
                        <Pressable
                          key={preset.key}
                          style={[styles.presetRow, { flexDirection: "row", alignItems: "center", gap: 8 }]}
                          onPress={() => applyPreset(preset)}
                        >
                          <Text style={[styles.customCheck, !isActive && { color: "transparent" }, { width: 14 }]}>✓</Text>
                          <Text style={[styles.presetText, isActive && { fontWeight: "700", color: "#1976d2" }]}>
                            {t(preset.labelKey)}
                          </Text>
                        </Pressable>
                      );
                    })}

                    <View style={styles.rangeDivider} />

                    <View style={styles.customContainer}>
                      <View style={styles.customDatesRow}>
                        <Text style={[styles.customCheck, activePresetKey !== "custom" && { color: "transparent" }, { width: 14 }]}>✓</Text>
                        <Text style={styles.customLabel}>{t("from")}</Text>

                        <View style={[styles.inputWrapper, !isMobile && { width: 120 }]}>
                          <TextInput
                            style={styles.customInputIconized}
                            value={fromText}
                            onChangeText={setFromText}
                            placeholder="DD/MM/YYYY"
                            placeholderTextColor={isDark ? "#777" : "#bbb"}
                          />
                          <Pressable
                            style={styles.calendarIconBtn}
                            onPress={() => setIsFromCalendarOpen(true)}
                          >
                            <Feather name="calendar" size={14} color="#1976d2" />
                          </Pressable>
                        </View>

                        <Text style={styles.customLabel}>{t("to")}</Text>

                        <View style={[styles.inputWrapper, !isMobile && { width: 120 }]}>
                          <TextInput
                            style={styles.customInputIconized}
                            value={toText}
                            onChangeText={setToText}
                            placeholder="DD/MM/YYYY"
                            placeholderTextColor={isDark ? "#777" : "#bbb"}
                          />
                          <Pressable
                            style={styles.calendarIconBtn}
                            onPress={() => setIsToCalendarOpen(true)}
                          >
                            <Feather name="calendar" size={14} color="#1976d2" />
                          </Pressable>
                        </View>
                      </View>

                      <Pressable style={styles.applyButton} onPress={applyCustomRange}>
                        <Text style={styles.applyButtonText}>{t("apply")}</Text>
                      </Pressable>
                    </View>
                  </View>
                </>
              )}
            </View>

            <Pressable style={[styles.navButton, isMobile && { paddingHorizontal: 8 }]} onPress={() => shiftWindow(1)}>
              <Text style={styles.navButtonText}>›</Text>
            </Pressable>
          </View>

          {isMobile && viewMode === "gantt" && loadMoreElement}
        </View>

        {!isMobile && viewMode === "gantt" && loadMoreElement}

        {!isMobile && filterChips}

        {!isMobile && <View style={{ flex: 1, minWidth: 16 }} />}

        {!isMobile && (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
            <Text style={styles.countText}>
              {viewMode === "list" ? listTotals.count : visibleCount} {t("productionRuns")}
            </Text>

            {viewToggleElement}

            <Pressable style={styles.refreshButton} onPress={refreshData}>
              <Feather name="refresh-cw" size={14} color={isDark ? "#aaa" : "#6B7280"} />
            </Pressable>
          </View>
        )}

        <View style={isMobile ? { width: "100%", flexDirection: "row", alignItems: "center", marginTop: 4 } : { flexDirection: "row", alignItems: "center", gap: 12 }}>
          {isMobile && (
            <Pressable style={[styles.refreshButton, { marginRight: 8 }]} onPress={refreshData}>
              <Feather name="refresh-cw" size={14} color={isDark ? "#aaa" : "#6B7280"} />
            </Pressable>
          )}

          {isMobile && (
            <Pressable
              style={[
                styles.downloadButton,
                { marginRight: 8, backgroundColor: showMobileFilters ? colors.primary : (isDark ? "#1E293B" : "#F8FAFC"), borderColor: showMobileFilters ? colors.primary : (isDark ? "#334155" : "#E2E8F0") }
              ]}
              onPress={() => setShowMobileFilters(!showMobileFilters)}
            >
              <Feather
                name="filter"
                size={14}
                color={showMobileFilters ? "#FFFFFF" : "#6B7280"}
              />
            </Pressable>
          )}

          <View style={[styles.searchContainer, isMobile && { flex: 1, width: "auto" }]}>
            <ScrollView
              ref={searchScrollRef}
              onContentSizeChange={() =>
                searchScrollRef.current?.scrollToEnd({ animated: true })
              }
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ flex: 1 }}
              contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}
            >
              {searchCriteria.map((term, index) => (
                <View key={`${term}-${index}`} style={styles.searchChip}>
                  <Text style={styles.searchChipText}>{term}</Text>
                  <Pressable
                    style={styles.searchChipRemove}
                    onPress={() =>
                      setSearchCriteria((current) => current.filter((_, i) => i !== index))
                    }
                  >
                    <Text style={styles.searchChipX}>✕</Text>
                  </Pressable>
                </View>
              ))}
              <TextInput
                style={styles.searchInputInner}
                value={searchInput}
                onChangeText={onSearchChange}
                placeholder={searchCriteria.length === 0 ? t("searchBar") : t("addCriteria")}
                placeholderTextColor="#aaa"
                onSubmitEditing={commitSearchInput}
                blurOnSubmit={false}
              />
            </ScrollView>
          </View>

          {!isMobile && (
            <Pressable style={styles.downloadButton} onPress={exportToExcel}>
              <Feather name="download" size={14} color={isDark ? "#aaa" : "#6B7280"} />
            </Pressable>
          )}
        </View>

        {isMobile && showMobileFilters && filterChips}
      </View>

      {isBusy ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1976d2" />
          <Text style={styles.loadingText}>{t("loading")}</Text>
        </View>
      ) : viewMode === "list" ? (
        listGroups.length === 0 ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>{t("noData")}</Text>
          </View>
        ) : isMobile ? (
          <ScrollView
            style={{ flex: 1 }}
            nestedScrollEnabled
            stickyHeaderIndices={mobileStickyIndices}
          >
            {mobileFlatItems.map((entry, index) => {
              if (entry.type === "header") {
                const isExpanded = expandedKeys.includes(entry.group.key);
                return (
                  <Pressable
                    key={`header-${entry.group.key}`}
                    style={[styles.mobileGroupHeader, index > 0 && { marginTop: 12 }]}
                    onPress={() => toggleExpanded(entry.group.key)}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.mobileGroupTitle} numberOfLines={1}>
                        {isExpanded ? "▼" : "▶"}  {entry.group.name} ({entry.group.runs.length})
                      </Text>
                    </View>
                    <Text style={styles.mobileGroupTotalText}>
                      {t("plannedQuantity")}: {formatQty(entry.group.plannedQty)}
                    </Text>
                  </Pressable>
                );
              }

              const { order, run } = entry.item;
              const statusColors = getStatusColors(String(order?.OrderStatus), isDark);
              return (
                <View key={entry.item.key} style={styles.mobileCard}>
                  <View style={styles.mobileCardHeader}>
                    <View style={{ flex: 1, paddingRight: 8 }}>
                      <Text style={styles.mobileCardTitle} numberOfLines={1}>
                        {order?.MSRef ?? "—"} · {order?.ItemCode ?? "—"}
                      </Text>
                      <Text style={styles.mobileCardSubtitle} numberOfLines={1}>
                        {order?.BPName ?? "—"}
                      </Text>
                    </View>
                    <View style={[styles.statusChip, { marginHorizontal: 0, backgroundColor: statusColors.bg }]}>
                      <Text style={[styles.statusChipText, { color: statusColors.text }]} numberOfLines={1}>
                        {STATUS_LABELS[order?.OrderStatus] ? t(STATUS_LABELS[order?.OrderStatus]) : order?.OrderStatus ?? "—"}
                      </Text>
                    </View>
                  </View>

                  <Text style={styles.mobileCardProduct} numberOfLines={2}>
                    {order?.ItemName ?? ""}
                  </Text>

                  <View style={styles.mobileCardFooter}>
                    <View style={styles.mobileMetaChip}>
                      <Feather name="hash" size={11} color={isDark ? colors.textSecondary : "#64748B"} />
                      <Text style={styles.mobileMetaChipText} numberOfLines={1}>
                        {t("runOrder")} {run?.runOrder ?? "—"}
                      </Text>
                    </View>
                    <View style={styles.mobileMetaChip}>
                      <Feather name="package" size={11} color={isDark ? colors.textSecondary : "#64748B"} />
                      <Text style={styles.mobileMetaChipText}>{formatQty(run?.plannedQty)}</Text>
                    </View>
                    <View style={styles.mobileMetaChip}>
                      <Feather name="calendar" size={11} color={isDark ? colors.textSecondary : "#64748B"} />
                      <Text style={styles.mobileMetaChipText}>
                        {formatTrackerDate(run?.actualStartDay ?? run?.planStartDay)} → {formatTrackerDate(run?.actualCompletionDate ?? run?.ESTCompletionDate)}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })}

            <View style={[styles.mobileTotalRow, { marginTop: 12 }]}>
              <Text style={styles.mobileTotalText}>
                {t("plannedQuantity")}: {formatQty(listTotals.plannedQty)}
              </Text>
            </View>
          </ScrollView>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator
            style={[
              {
                flexGrow: 0,
                backgroundColor: colors.surface,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: isDark ? "#333" : "#E2E8F0",
                overflow: "hidden",
              },
              CONTAIN_OVERSCROLL_X,
            ]}
            contentContainerStyle={{ minWidth: "100%" }}
          >
            <View>
              <View
                style={styles.listHeaderRow}
                onLayout={(event) => setListHeaderH(event.nativeEvent.layout.height)}
              >
                {LIST_COLUMNS.map((column, index) => (
                  <View key={column.key} style={{ width: columnWidth(column.key) }}>
                    <Text
                      style={[styles.listHeaderText, { textAlign: column.align }]}
                      numberOfLines={1}
                    >
                      {t(column.labelKey)}
                    </Text>
                    {Platform.OS === "web" && (
                      <View
                        style={RESIZE_HANDLE}
                        {...({
                          onMouseDown: (event: any) =>
                            startResize(event, column.key, columnWidth(column.key)),
                        } as any)}
                      />
                    )}
                    {Platform.OS === "web" && index > 0 && (
                      <View
                        style={RESIZE_HANDLE_LEFT}
                        {...({
                          onMouseDown: (event: any) =>
                            startResize(
                              event,
                              LIST_COLUMNS[index - 1].key,
                              columnWidth(LIST_COLUMNS[index - 1].key),
                            ),
                        } as any)}
                      />
                    )}
                  </View>
                ))}
              </View>

              <View ref={listRowsRef} onLayout={measureChartTops} />
              <ScrollView
                style={[{ maxHeight: listRowsMaxHeight }, CONTAIN_OVERSCROLL_Y]}
                nestedScrollEnabled
              >
                {listGroups.map((group) => {
                  const isExpanded = expandedKeys.includes(group.key);
                  return (
                    <View key={group.key}>
                      <Pressable
                        style={styles.listGroupRow}
                        onPress={() => toggleExpanded(group.key)}
                      >
                        <Text style={[styles.listGroupLabel, { width: listLabelWidth }]}>
                          {isExpanded ? "▼" : "▶"}  {group.name} ({group.runs.length})
                        </Text>
                        <Text style={[styles.listGroupCell, { width: columnWidth("plannedQty") }]}>
                          {formatQty(group.plannedQty)}
                        </Text>
                        <View style={{ width: listTailWidth }} />
                      </Pressable>

                      {isExpanded &&
                        group.runs.map(({ key, order, run }, index) => {
                          const statusColors = getStatusColors(String(order?.OrderStatus), isDark);
                          const cell = (columnKey: string, value: any, right = false) => (
                            <Text
                              style={[
                                styles.listCell,
                                right && styles.listCellRight,
                                { width: columnWidth(columnKey) },
                              ]}
                              numberOfLines={1}
                            >
                              {value === null || value === undefined || value === "" ? "—" : value}
                            </Text>
                          );
                          return (
                            <View
                              key={key}
                              style={[styles.listRow, index % 2 === 1 && styles.listRowAlt]}
                            >
                              {cell("msRef", order?.MSRef)}
                              {cell("orderNumber", order?.OrderNumber1)}
                              {cell("itemCode", order?.ItemCode)}
                              {cell("itemName", order?.ItemName)}
                              {cell("customer", order?.BPName)}
                              {cell("runOrder", run?.runOrder, true)}
                              {cell("plannedQty", formatQty(run?.plannedQty), true)}
                              {cell("planStartDay", formatTrackerDate(run?.planStartDay))}
                              {cell("estCompletion", formatTrackerDate(run?.ESTCompletionDate))}
                              {cell("actualStartDay", formatTrackerDate(run?.actualStartDay))}
                              {cell("actualCompletionDate", formatTrackerDate(run?.actualCompletionDate))}
                              {cell("etd", formatTrackerDate(run?.ETD))}
                              {cell("eta", formatTrackerDate(run?.ETA))}
                              {cell("dueDate", order?.RequestDate)}
                              <View style={{ width: columnWidth("status"), justifyContent: "center" }}>
                                <View style={[styles.statusChip, { backgroundColor: statusColors.bg }]}>
                                  <Text style={[styles.statusChipText, { color: statusColors.text }]} numberOfLines={1}>
                                    {STATUS_LABELS[order?.OrderStatus] ? t(STATUS_LABELS[order?.OrderStatus]) : order?.OrderStatus ?? "—"}
                                  </Text>
                                </View>
                              </View>
                            </View>
                          );
                        })}
                    </View>
                  );
                })}
              </ScrollView>

              <View
                style={styles.listTotalRow}
                onLayout={(event) => setListFooterH(event.nativeEvent.layout.height)}
              >
                <Text style={[styles.listGroupLabel, { width: listLabelWidth }]}>
                  {t("total")}
                </Text>
                <Text style={[styles.listTotalCell, { width: columnWidth("plannedQty") }]}>
                  {formatQty(listTotals.plannedQty)}
                </Text>
                <View style={{ width: listTailWidth }} />
              </View>
            </View>
          </ScrollView>
        )
      ) : rows.length === 0 ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t("noData")}</Text>
        </View>
      ) : (
        <>
          <View ref={headerRowRef} style={styles.headerRow}>
            <View style={[styles.cornerCell, { width: LEFT_W }]}>
              <Text style={styles.cornerText}>{t("productionLine")}</Text>
            </View>
            <View style={styles.headerClip}>
              <Animated.View
                style={{ width: gridWidth, transform: [{ translateX: negScrollAnim }] }}
              >
                <View style={styles.monthRow}>
                  {months.map((month, index) => {
                    const monthWidth = month.span * DAY_W;
                    const maxTranslate = Math.max(0, monthWidth - 110);
                    const translateX = scrollAnim.interpolate({
                      inputRange: [
                        -1,
                        month.startX,
                        month.startX + maxTranslate,
                        month.startX + maxTranslate + 1,
                      ],
                      outputRange: [0, 0, maxTranslate, maxTranslate],
                      extrapolate: "clamp",
                    });

                    return (
                      <View
                        key={`${month.label}-${index}`}
                        style={[styles.monthCell, { width: monthWidth }]}
                      >
                        <Animated.Text
                          numberOfLines={1}
                          style={[styles.monthText, { transform: [{ translateX }] }]}
                        >
                          {month.label}
                        </Animated.Text>
                      </View>
                    );
                  })}
                </View>
                <View style={styles.dayRow}>
                  {days.map(({ date, isWeekend }, index) => (
                    <View
                      key={index}
                      style={[
                        styles.dayCell,
                        isWeekend && styles.weekendCell,
                        index === todayIndex && styles.todayCell,
                      ]}
                    >
                      <Text style={styles.dayText}>
                        {String(date.getDate()).padStart(2, "0")}
                      </Text>
                    </View>
                  ))}
                </View>
              </Animated.View>
            </View>
          </View>

          <View ref={bodyScrollRef} onLayout={measureChartTops} />
          <ScrollView
            style={[{ height: availableBodyHeight, flexGrow: 0, flexShrink: 0 }, CONTAIN_OVERSCROLL_Y]}
            onLayout={(event) => setBodyHeight(event.nativeEvent.layout.height)}
            nestedScrollEnabled
          >
            <View style={{ flexDirection: "row" }}>
              <View ref={labelColumnRef} style={{ width: LEFT_W }}>
                {rows.map((row) => (
                  <View key={row.key} style={[styles.labelCell, { height: row.height }]}>
                    <Text style={styles.labelText}>{row.name}</Text>
                  </View>
                ))}
              </View>

              <ScrollView
                ref={gridScrollRef}
                horizontal
                style={CONTAIN_OVERSCROLL_X}
                onLayout={(event) => setGridViewWidth(event.nativeEvent.layout.width)}
                onScroll={onBodyScroll}
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator
              >
                <View style={{ width: gridWidth, height: gridHeight }}>
                  {rows.map((row) => (
                    <View key={row.key} style={[styles.gridRow, { height: row.height }]}>
                      {days.map(({ isWeekend }, index) => (
                        <View
                          key={index}
                          style={[
                            styles.gridCell,
                            { left: index * DAY_W },
                            isWeekend && styles.weekendCell,
                            index === todayIndex && styles.todayCell,
                          ]}
                        />
                      ))}

                      {row.bars.map(({ item, startIndex, endIndex, lane }) => {
                        const barTop = row.barOffset + ROW_PAD + lane * (BAR_H + BAR_GAP);
                        const isComplete = Boolean(item.run?.actualCompletionDate);
                        return (
                          <Pressable
                            key={item.key}
                            onPress={(event) => {
                              const barLeft = startIndex * DAY_W;
                              const barRight = (endIndex + 1) * DAY_W;
                              const viewLeft = scrollXRef.current;
                              const viewRight = viewLeft + (gridViewWidth || gridWidth);
                              const clickX = event.nativeEvent?.locationX;
                              const anchorX =
                                typeof clickX === "number"
                                  ? barLeft + clickX
                                  : Math.max(barLeft, viewLeft) + DAY_W / 2;
                              setSelected({
                                item,
                                barTop: row.top + barTop,
                                barBottom: row.top + barTop + BAR_H,
                                anchorX: Math.min(Math.max(anchorX, barLeft), barRight),
                                viewLeft,
                                viewRight,
                              });
                            }}
                            style={[
                              styles.bar,
                              {
                                left: startIndex * DAY_W + 1,
                                width: (endIndex - startIndex + 1) * DAY_W - 2,
                                top: barTop,
                                backgroundColor: colorFor(
                                  item.order?.ItemCode ?? item.key,
                                ),
                                opacity: isComplete ? 0.5 : 1,
                                borderColor:
                                  selected?.item.key === item.key ? "#1976d2" : "transparent",
                              },
                            ]}
                          >
                            <Text style={styles.barText} numberOfLines={1}>
                              {item.order?.MSRef} · {item.order?.ItemName}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  ))}

                  {selected &&
                    (() => {
                      const flipAbove =
                        bodyHeight > 0 &&
                        selected.barBottom + CARET_H + popoverHeight > bodyHeight &&
                        selected.barTop - CARET_H - popoverHeight >= 0;
                      const popoverTop = flipAbove
                        ? selected.barTop - CARET_H - popoverHeight
                        : selected.barBottom + CARET_H;
                      const minLeft = Math.max(0, selected.viewLeft + 8);
                      const maxLeft = Math.min(
                        gridWidth - POPOVER_W,
                        selected.viewRight - POPOVER_W - 8,
                      );
                      const popoverLeft = Math.max(
                        minLeft,
                        Math.min(selected.anchorX - 24, Math.max(minLeft, maxLeft)),
                      );
                      const caretLeft = Math.max(
                        10,
                        Math.min(selected.anchorX - popoverLeft - CARET_H, POPOVER_W - 24),
                      );
                      const { order, run } = selected.item;

                      return (
                        <>
                        {Platform.OS !== "web" && (
                          <Pressable
                            style={StyleSheet.absoluteFill}
                            onPress={() => setSelected(null)}
                          />
                        )}
                        <View
                          ref={popoverRef}
                          style={[styles.popover, { top: popoverTop, left: popoverLeft }]}
                          onLayout={(event) =>
                            setPopoverHeight(event.nativeEvent.layout.height)
                          }
                        >
                          <View
                            style={[
                              flipAbove ? styles.popoverCaretBottom : styles.popoverCaret,
                              { left: caretLeft },
                            ]}
                          />
                          <View style={styles.popoverHeader}>
                            <Text style={styles.popoverTitle}>
                              {[order?.ItemCode, order?.ItemName, order?.MSRef]
                                .filter(Boolean)
                                .join(" - ")}
                            </Text>
                            <Pressable
                              style={styles.popoverClose}
                              onPress={() => setSelected(null)}
                            >
                              <Text style={styles.popoverCloseText}>✕</Text>
                            </Pressable>
                          </View>
                          <View style={styles.popoverBody}>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("customer")}: </Text>
                              {order?.BPName ?? t("unknown")}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("orderNumber")}: </Text>
                              {order?.OrderNumber1 ?? t("unknown")}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("runOrder")}: </Text>
                              {run?.runOrder ?? "—"}
                              {"   "}
                              <Text style={styles.popoverLabel}>
                                {t("plannedQuantity")}:{" "}
                              </Text>
                              {run?.plannedQty?.toLocaleString?.() ?? "—"}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("planStartDay")}: </Text>
                              {formatTrackerDate(run?.planStartDay)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("estCompletion")}: </Text>
                              {formatTrackerDate(run?.ESTCompletionDate)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("actualStartDay")}: </Text>
                              {formatTrackerDate(run?.actualStartDay)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>
                                {t("actualCompletionDate")}:{" "}
                              </Text>
                              {formatTrackerDate(run?.actualCompletionDate)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("etd")}: </Text>
                              {formatTrackerDate(run?.ETD)}
                              {"   "}
                              <Text style={styles.popoverLabel}>{t("eta")}: </Text>
                              {formatTrackerDate(run?.ETA)}
                            </Text>
                            <Text style={styles.popoverRow}>
                              <Text style={styles.popoverLabel}>{t("dueDate")}: </Text>
                              {order?.RequestDate ?? "—"}
                              {"   "}
                              <Text style={styles.popoverLabel}>{t("orderStatus")}: </Text>
                              {order?.OrderStatus ?? "—"}
                            </Text>
                          </View>
                        </View>
                        </>
                      );
                    })()}
                </View>
              </ScrollView>
            </View>
          </ScrollView>
        </>
      )}

      <View style={styles.modalHost} pointerEvents="box-none">
        <PaperProvider theme={{
          ...(isDark ? DarkTheme : DefaultTheme),
          colors: {
            ...(isDark ? DarkTheme.colors : DefaultTheme.colors),
            primary: "#1976d2",
          },
        }}>
          <DatePickerModal
            locale="en"
            mode="single"
            visible={isFromCalendarOpen}
            onDismiss={() => setIsFromCalendarOpen(false)}
            date={parseDMY(fromText) || new Date()}
            onConfirm={onConfirmFromDate}
          />
          <DatePickerModal
            locale="en"
            mode="single"
            visible={isToCalendarOpen}
            onDismiss={() => setIsToCalendarOpen(false)}
            date={parseDMY(toText) || new Date()}
            onConfirm={onConfirmToDate}
          />
        </PaperProvider>
      </View>
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? colors.background : "#f5f5f5",
      padding: 12,
    },
    hidden: {
      display: "none",
    },
    viewToggle: {
      flexDirection: "row",
      backgroundColor: isDark ? "#1E293B" : "#F1F5F9",
      borderRadius: 8,
      padding: 4,
      gap: 2,
    },
    viewButton: {
      paddingHorizontal: 16,
      paddingVertical: 6,
      borderRadius: 6,
      backgroundColor: "transparent",
    },
    viewButtonActive: {
      backgroundColor: "#56b7fd",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    viewButtonText: {
      fontSize: 12,
      fontWeight: "500",
      color: isDark ? "#94A3B8" : "#64748B",
    },
    viewButtonTextActive: {
      color: "#fff",
      fontWeight: "600",
    },
    listHeaderRow: {
      flexDirection: "row",
      alignItems: "stretch",
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    listHeaderText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
      paddingHorizontal: 6,
    },
    listGroupRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderColor: colors.border,
      backgroundColor: isDark ? "#1c1c1c" : "#fafafa",
    },
    listGroupLabel: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      paddingHorizontal: 6,
    },
    listGroupCell: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      textAlign: "right",
      paddingHorizontal: 6,
    },
    listRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderColor: colors.borderSubtle,
    },
    listRowAlt: {
      backgroundColor: isDark ? "#171717" : "#fcfcfc",
    },
    listCell: {
      fontSize: 11,
      color: colors.text,
      paddingHorizontal: 6,
    },
    listCellRight: {
      textAlign: "right",
    },
    statusChip: {
      alignSelf: "flex-start",
      marginHorizontal: 6,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 10,
    },
    statusChipText: {
      fontSize: 10,
      fontWeight: "600",
    },
    listTotalRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
      borderTopWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    listTotalCell: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      textAlign: "right",
      paddingHorizontal: 6,
    },
    mobileGroupHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E5E7EB",
      backgroundColor: isDark ? "#1c1c1c" : "#FFFFFF",
      gap: 8,
      shadowColor: isDark ? "#000" : "#1E293B",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      elevation: 2,
      zIndex: 1,
    },
    mobileGroupTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    mobileGroupTotalText: {
      fontSize: 10,
      color: colors.textSecondary,
    },
    mobileCard: {
      marginTop: 8,
      marginHorizontal: 4,
      padding: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E2E8F0",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      shadowColor: isDark ? "#000" : "#1E293B",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.06,
      shadowRadius: 6,
      elevation: 2,
    },
    mobileCardHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
    },
    mobileCardTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    mobileCardSubtitle: {
      fontSize: 11,
      color: colors.textSecondary,
      marginTop: 2,
    },
    mobileCardProduct: {
      fontSize: 12,
      color: colors.text,
      marginTop: 8,
    },
    mobileCardFooter: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 10,
    },
    mobileMetaChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E2E8F0",
      backgroundColor: isDark ? "#171717" : "#F8FAFC",
      maxWidth: "100%",
    },
    mobileMetaChipText: {
      fontSize: 10,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    mobileTotalRow: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 16,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderTopWidth: 1,
      borderColor: colors.border,
    },
    mobileTotalText: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
    },
    toolbar: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: 16,
      position: "relative",
      zIndex: 50,
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 14,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: isDark ? 0.3 : 0.08,
      shadowRadius: 3,
      elevation: 2,
      borderWidth: isDark ? 1 : 0,
      borderColor: isDark ? "#333" : "transparent",
    },
    title: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    rangeGroup: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    navButton: {
      width: 28,
      alignItems: "center",
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    navButtonText: {
      fontSize: 16,
      lineHeight: 20,
      color: colors.text,
    },
    rangeFieldWrapper: {
      position: "relative",
    },
    rangeField: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    rangeFieldText: {
      fontSize: 12,
      color: colors.text,
    },
    rangeBackdrop: {
      position: "absolute",
      top: -1000,
      left: -1000,
      right: -1000,
      bottom: -1000,
      zIndex: 29,
    },
    rangeDropdown: {
      position: "absolute",
      top: "100%",
      marginTop: 4,
      minWidth: 200,
      zIndex: 30,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      paddingVertical: 6,
      shadowColor: "#000",
      shadowOpacity: 0.2,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    presetRow: {
      paddingHorizontal: 16,
      paddingVertical: 10,
    },
    presetText: {
      fontSize: 13,
      color: colors.text,
    },
    rangeDivider: {
      height: 1,
      backgroundColor: isDark ? "#333" : "#F1F5F9",
      marginVertical: 8,
    },
    customContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      paddingHorizontal: 12,
      paddingVertical: 10,
      gap: 8,
    },
    customDatesRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    customCheck: {
      fontSize: 14,
      fontWeight: "800",
      color: "#1976d2",
    },
    customLabel: {
      fontSize: 12,
      color: isDark ? "#9CA3AF" : "#64748B",
    },
    inputWrapper: {
      flexDirection: "row",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#444" : "#E2E8F0",
      borderRadius: 4,
      backgroundColor: isDark ? "#1e1e1e" : "#fff",
      width: 96,
      overflow: "hidden",
    },
    customInputIconized: {
      flex: 1,
      minWidth: 0,
      paddingHorizontal: 4,
      paddingVertical: 4,
      fontSize: 11,
      color: colors.text,
      backgroundColor: "transparent",
      ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
    },
    calendarIconBtn: {
      paddingHorizontal: 6,
      paddingVertical: 6,
      justifyContent: "center",
      alignItems: "center",
      borderLeftWidth: 1,
      borderLeftColor: isDark ? "#444" : "#E2E8F0",
      backgroundColor: isDark ? "#2A2A2A" : "#F8FAFC",
      ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
    },
    applyButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 4,
      backgroundColor: "#1976d2",
    },
    applyButtonText: {
      fontSize: 12,
      fontWeight: "700",
      color: "#fff",
    },
    chipGroup: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 14,
      paddingVertical: 6,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? "#333" : "#E2E8F0",
      backgroundColor: isDark ? "#1F2937" : "#F4F6F8",
    },

    chipActive: {
      borderColor: "#56B7FD",
      backgroundColor: "#56B7FD",
    },
    chipText: {
      fontSize: 12,
      fontWeight: "500",
      color: isDark ? "#9CA3AF" : "#475569",
    },
    chipTextActive: {
      color: "#FFFFFF",
      fontWeight: "600",
    },
    loadMoreButton: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: "#1976d2",
      backgroundColor: isDark ? "#123" : "#e3f2fd",
    },
    loadMoreText: {
      fontSize: 11,
      fontWeight: "700",
      color: "#1976d2",
    },
    countText: {
      fontSize: 11,
      color: colors.textMuted,
    },
    searchContainer: {
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#444" : "#D1D5DB",
      borderRadius: 8,
      paddingHorizontal: 8,
      paddingVertical: 4,
      backgroundColor: colors.surface,
      minHeight: 36,
      width: 320,
    },
    searchChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.primary + "33" : "#DBEAFE",
      borderRadius: 20,
      paddingLeft: 10,
      paddingRight: 6,
      paddingVertical: 4,
      marginRight: 4,
      marginVertical: 2,
    },
    searchChipText: {
      fontSize: 12,
      color: isDark ? "#93C5FD" : "#1D4ED8",
      fontWeight: "600",
    },
    searchChipRemove: {
      marginLeft: 6,
      width: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: isDark ? colors.primary + "66" : "#93C5FD",
      justifyContent: "center",
      alignItems: "center",
    },
    searchChipX: {
      fontSize: 9,
      color: isDark ? "#fff" : "#1E40AF",
      fontWeight: "700",
      lineHeight: 16,
    },
    searchInputInner: {
      flex: 1,
      minWidth: 120,
      fontSize: 13,
      height: 24,
      padding: 0,
      color: colors.text,
    },
    downloadButton: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: isDark ? "#1E293B" : "#F8FAFC",
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#E2E8F0",
    },
    refreshButton: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: isDark ? "#1E293B" : "#F8FAFC",
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: isDark ? "#334155" : "#E2E8F0",
    },
    modalHost: {
      position: "absolute",
      width: 0,
      height: 0,
      overflow: "visible",
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    loadingText: {
      marginTop: 10,
      fontSize: 12,
      color: colors.textMuted,
    },
    headerRow: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    headerClip: {
      flex: 1,
      overflow: "hidden",
    },
    cornerCell: {
      height: MONTH_H + DAY_H,
      justifyContent: "center",
      paddingLeft: 8,
      borderRightWidth: 1,
      borderColor: colors.border,
    },
    cornerText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    monthRow: {
      flexDirection: "row",
      height: MONTH_H,
    },
    monthCell: {
      height: MONTH_H,
      justifyContent: "center",
      paddingLeft: 6,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    monthText: {
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
    },
    dayRow: {
      flexDirection: "row",
      height: DAY_H,
    },
    dayCell: {
      width: DAY_W,
      height: DAY_H,
      justifyContent: "center",
      alignItems: "center",
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    dayText: {
      fontSize: 10,
      color: colors.textMuted,
    },
    weekendCell: {
      backgroundColor: isDark ? "#1b1b1b" : "#eeeeee",
    },
    todayCell: {
      backgroundColor: isDark ? "#2a2410" : "#fff8e1",
    },
    labelCell: {
      justifyContent: "center",
      paddingLeft: 8,
      borderLeftWidth: 1,
      borderBottomWidth: 1,
      borderRightWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    labelText: {
      fontSize: 12,
      color: colors.text,
    },
    gridRow: {
      position: "relative",
      borderBottomWidth: 1,
      borderColor: colors.border,
    },
    gridCell: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: DAY_W,
      borderRightWidth: 1,
      borderColor: colors.borderSubtle,
    },
    bar: {
      position: "absolute",
      height: BAR_H,
      borderRadius: 3,
      borderWidth: 1,
      justifyContent: "center",
      paddingHorizontal: 5,
      overflow: "hidden",
    },
    barText: {
      fontSize: 10,
      color: "#2b2b2b",
    },
    popover: {
      position: "absolute",
      width: POPOVER_W,
      zIndex: 20,
      borderRadius: 6,
      backgroundColor: isDark ? "#2b2b2b" : "#ffffff",
      shadowColor: "#000",
      shadowOpacity: 0.25,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 4 },
      elevation: 8,
    },
    popoverCaret: {
      position: "absolute",
      top: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderBottomWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverCaretBottom: {
      position: "absolute",
      bottom: -CARET_H,
      width: 0,
      height: 0,
      borderLeftWidth: CARET_H,
      borderRightWidth: CARET_H,
      borderTopWidth: CARET_H,
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderTopColor: isDark ? "#2b2b2b" : "#ffffff",
    },
    popoverHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      padding: 12,
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      backgroundColor: isDark ? "#3a3a3a" : "#e9e9ea",
    },
    popoverTitle: {
      flex: 1,
      fontSize: 13,
      fontWeight: "700",
      lineHeight: 18,
      color: colors.text,
      paddingRight: 8,
    },
    popoverClose: {
      paddingHorizontal: 4,
    },
    popoverCloseText: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
    },
    popoverBody: {
      padding: 12,
      gap: 4,
    },
    popoverRow: {
      fontSize: 12,
      color: colors.text,
    },
    popoverLabel: {
      fontWeight: "700",
      color: colors.text,
    },
  });
